import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all lessons
 */
export const fetchLessons = createAsyncThunk(
  'lessons/fetchLessons',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Courses.app_courses_lessons_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch lessons');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lessons');
    }
  }
);

/**
 * Fetch a single lesson by ID
 */
export const fetchLessonById = createAsyncThunk(
  'lessons/fetchLessonById',
  async ({ token, lessonId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Courses.app_courses_lessons_get,
        lessonId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          ...response.data.data,
          status: response.data.data.status === "active" ? true : false,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch lesson');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lesson');
    }
  }
);

/**
 * Create a new lesson
 */
export const createLesson = createAsyncThunk(
  'lessons/createLesson',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'status') {
          data.append('status', value ? 'active' : 'inactive');
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Courses.app_courses_lessons_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create lesson');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create lesson';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing lesson
 */
export const updateLesson = createAsyncThunk(
  'lessons/updateLesson',
  async ({ token, lessonId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        if (key === 'status') {
          if (currentValue !== initialValue) {
            data.append('status', currentValue ? 'active' : 'inactive');
          }
        }
        else if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Courses.app_courses_lessons_update,
        lessonId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update lesson');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update lesson';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a lesson
 */
export const deleteLesson = createAsyncThunk(
  'lessons/deleteLesson',
  async ({ token, lessonId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Courses.app_courses_lessons_delete,
        lessonId
      );
      if (response.data.status === 200) {
        return lessonId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete lesson');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete lesson');
    }
  }
);

const initialState = {
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  selectedEntity: null,
  initialFormData: null,
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  formErrors: {},
};

const lessonSlice = createSlice({
  name: 'lessons',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLessons.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchLessons.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchLessons.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      })
      .addCase(fetchLessonById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchLessonById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchLessonById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      })
      .addCase(createLesson.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createLesson.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createLesson.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create lesson';
        state.formErrors = errorData.errors || {};
      })
      .addCase(updateLesson.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateLesson.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateLesson.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update lesson';
        state.formErrors = errorData.errors || {};
      })
      .addCase(deleteLesson.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteLesson.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(lesson => lesson.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteLesson.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = lessonSlice.actions;

export const selectLessons = (state) => state.lessons.entities;
export const selectLessonsPagination = (state) => state.lessons.pagination;
export const selectSelectedLesson = (state) => state.lessons.selectedEntity;
export const selectInitialFormData = (state) => state.lessons.initialFormData;
export const selectLessonsListStatus = (state) => state.lessons.listStatus;
export const selectLessonDetailStatus = (state) => state.lessons.detailStatus;
export const selectLessonCreateStatus = (state) => state.lessons.createStatus;
export const selectLessonUpdateStatus = (state) => state.lessons.updateStatus;
export const selectLessonDeleteStatus = (state) => state.lessons.deleteStatus;
export const selectLessonsListError = (state) => state.lessons.listError;
export const selectLessonDetailError = (state) => state.lessons.detailError;
export const selectLessonCreateError = (state) => state.lessons.createError;
export const selectLessonUpdateError = (state) => state.lessons.updateError;
export const selectLessonDeleteError = (state) => state.lessons.deleteError;
export const selectLessonFormErrors = (state) => state.lessons.formErrors;

export default lessonSlice.reducer;
